import { useCallback, useState } from 'react';
import type { JSX } from 'react';
import type { CampaignCalendar } from '@roll1/shared';
import { CalendarDays } from 'lucide-react';
import { useSessionContext } from '../../contexts/SessionContext.js';
import { useI18n } from '../../i18n';
import { generateCalendarFromDescription } from '../../utils/calendar-generator';
import CalendarSettings from '../CalendarSettings';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * The campaign calendar in Preparation: the same window as at the table (chronicle → «⋯» → «Календарь»),
 * from the top bar — a calendar is set up before the game, not in the middle of it.
 */
export function StudioCalendar(): JSX.Element | null {
  const session = useSessionContext();
  const { t } = useI18n();
  const [open, setOpen] = useState<boolean>(false);
  const [aiPreview, setAiPreview] = useState<CampaignCalendar | null>(null);
  const send = session.mp.send as unknown as (msg: Record<string, unknown>) => void;
  const setCalendar = useCallback((calendar: CampaignCalendar): void => send({ type: 'calendar:set', calendar }), [send]);
  const removeCalendar = useCallback((): void => send({ type: 'calendar:remove' }), [send]);
  if (session.mp.serverState === null || session.mp.serverState === undefined) return null;
  const calendar = session.mp.serverState.calendar;
  const name = calendar?.name ?? t('timeline.panel.noCalendar');
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="studio-topbar__calendar text-meta text-muted-foreground hover:border-hl/40 hover:text-fg"
        data-testid="studio-calendar-open"
        onClick={(): void => setOpen(true)}
      >
        <CalendarDays size={14} aria-hidden="true" />
        <span className="max-w-40 truncate">{t('timeline.panel.calendarLine').replace('{name}', name)}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md" className="max-h-[calc(100dvh-4rem)] overflow-y-auto" data-testid="studio-calendar-dialog">
          <DialogHeader>
            <DialogTitle>{t('timeline.panel.calendarLine').replace('{name}', name)}</DialogTitle>
          </DialogHeader>
          <CalendarSettings
            calendar={calendar}
            onSetCalendar={setCalendar}
            onRemoveCalendar={removeCalendar}
            aiAvailable
            onRequestGeneration={(description: string): void => setAiPreview(generateCalendarFromDescription(description))}
            aiPreviewCalendar={aiPreview}
            onDone={(): void => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default StudioCalendar;
